// studioApi — the HypeFlyer Print Shop's one door to the API (2026-10-31).
//
// The print shop makes the same things the website makes (images, posts, Who
// Is cards, press kits) through THE SAME ENDPOINTS: nothing here is a new
// server path, so a picture made at the counter lands in the same gallery, a
// post in the same feed, and a charge on the same wallet, at the same price.
//
// Every call answers { ok, status, data } and never throws for an HTTP error:
// the studio has to tell four refusals apart, and two of them are not errors
// by status (see `refusal`).
import { API_BASE, AUTH_TOKEN } from '../../config';

const auth = () => (AUTH_TOKEN ? { Authorization: `Bearer ${AUTH_TOKEN}` } : {});

/**
 * One request. `body` is JSON unless `form` is a FormData (an upload: the
 * browser sets the multipart boundary itself, so no Content-Type here).
 */
export async function call(method, path, body = undefined, { form = null, signal } = {}) {
  const headers = { ...auth() };
  let payload;
  if (form) payload = form;
  else if (body !== undefined) { headers['Content-Type'] = 'application/json'; payload = JSON.stringify(body); }
  try {
    const res = await fetch(`${API_BASE}${path}`, { method, headers, body: payload, signal });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok && data?.success !== false, status: res.status, data: data || {} };
  } catch (err) {
    if (err?.name === 'AbortError') return { ok: false, status: 0, data: { error: 'Stopped' }, aborted: true };
    return { ok: false, status: 0, data: { error: 'Could not reach HypeTunes — check your connection and try again.' } };
  }
}

export const get = (path, opts) => call('GET', path, undefined, opts);
export const post = (path, body, opts) => call('POST', path, body ?? {}, opts);
export const patch = (path, body, opts) => call('PATCH', path, body ?? {}, opts);
export const put = (path, body, opts) => call('PUT', path, body ?? {}, opts);

/** Upload one file as multipart. `fields` are extra form fields; `field` is the file's field name ('image' unless told). */
export function upload(path, file, fields = {}, { field = 'image', signal } = {}) {
  const fd = new FormData();
  fd.append(field, file);
  for (const [k, v] of Object.entries(fields)) if (v != null) fd.append(k, String(v));
  return call('POST', path, undefined, { form: fd, signal });
}

/**
 * What a refusal MEANS, from the shapes the API uses everywhere:
 *   upgrade  — HTTP 200 { success:false, requiresUpgrade } (⚠ a paywall answers 200),
 *              or 403 { upgradeRequired } (image animate), or 403 code PLAN_REQUIRED
 *   credits  — 402 { required, available }
 *   nsfw     — 422 code NSFW_CONTENT_DETECTED
 *   identity — 409 code identity_needed (a brand-new face with no gender recorded)
 *   error    — anything else
 * Returns null for a success.
 */
export function refusal(r) {
  if (!r || r.ok) return null;
  const d = r.data || {};
  if (d.requiresUpgrade || d.upgradeRequired || d.code === 'PLAN_REQUIRED') {
    return { kind: 'upgrade', title: d.title || 'Upgrade to keep making', message: d.message || d.reason || d.error || 'This needs a paid plan.' };
  }
  if (r.status === 402) {
    const need = Number(d.required); const have = Number(d.available);
    const message = Number.isFinite(need) && Number.isFinite(have)
      ? `Not enough credits — this needs ${need}, you have ${have}.`
      : (d.error || 'Not enough credits.');
    return { kind: 'credits', message, required: Number.isFinite(need) ? need : null, available: Number.isFinite(have) ? have : null };
  }
  if (d.code === 'NSFW_CONTENT_DETECTED') return { kind: 'nsfw', message: d.reason ? `That image can't be used: ${d.reason}` : "That image can't be used here." };
  if (d.code === 'identity_needed') return { kind: 'identity', message: d.error || 'Tell us how this artist presents first (their gender presentation), then make the headshot.', field: d.field || null };
  return { kind: 'error', message: d.error || d.message || `Something went wrong (${r.status || 'offline'}).` };
}

/**
 * A price from the rate table: { credits, display } for priceLine, or null.
 * Cached for the visit — prices change by migration, not by the minute.
 */
const rates = new Map();
export async function rateOf(actionKey) {
  if (!actionKey) return null;
  if (rates.has(actionKey)) return rates.get(actionKey);
  const p = get(`/api/subscriptions/credits/rates/${encodeURIComponent(actionKey)}`).then((r) => {
    if (!r.ok) return null;
    const credits = Number(r.data.amount ?? r.data.credits);
    return Number.isFinite(credits) ? { credits, display: r.data.display || null } : null;
  });
  rates.set(actionKey, p);
  const v = await p;
  if (v == null) rates.delete(actionKey);   // a miss is tried again next time
  return v;
}

/** The wallet balance in credits, or null. */
export async function balance() {
  const r = await get('/api/subscriptions/credits/balance');
  const n = Number(r.data?.balance);
  return r.ok && Number.isFinite(n) ? n : null;
}

/** Poll `check()` every `everyMs` until it returns a truthy value, `tries` run out, or `signal` aborts. Resolves the value, or null. */
export function pollUntil(check, { everyMs = 8000, tries = 30, signal } = {}) {
  return new Promise((resolve) => {
    let n = 0;
    let timer = null;
    const stop = () => { if (timer) clearTimeout(timer); resolve(null); };
    signal?.addEventListener?.('abort', stop, { once: true });
    const tick = async () => {
      if (signal?.aborted) return;
      n += 1;
      let v = null;
      try { v = await check(n); } catch { v = null; }
      if (signal?.aborted) return;
      if (v) { signal?.removeEventListener?.('abort', stop); resolve(v); return; }
      if (n >= tries) { signal?.removeEventListener?.('abort', stop); resolve(null); return; }
      timer = setTimeout(tick, everyMs);
    };
    timer = setTimeout(tick, everyMs);
  });
}
